import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import {
    ArrowLeft,
    ChevronDown,
    Home,
    Inbox,
    KeyRound,
    LogOut,
    Plus,
    Search,
    Settings,
    Check,
    WifiOff,
    UploadCloud,
    Loader2,
    Cloud,
    CloudOff,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { buildSwitcherEntries } from "../lib/switcherEntries";
import { useNetworkStatus, useOfflineQueue } from "../lib/useOfflineQueue";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "./ui/dropdown-menu";
import CaptureFab from "./CaptureFab";
import HeaderAddMenu from "./HeaderAddMenu";
import FlagBell from "./FlagBell";
import FlagPanel from "./FlagPanel";
import BackupSafetyModal from "./BackupSafetyModal";
import ThemeToggle from "./ThemeToggle";
import GlobalSearchDialog from "./GlobalSearchDialog";
import ArchiveBanner from "./ArchiveBanner";
import OnSetFlowWordmark from "./OnSetFlowWordmark";
import { CaptureProvider, useCaptureContext } from "../context/CaptureContext";
import { useRouteBase, useApiBase } from "../context/ApiBaseContext";

function TopBar({ projectName, subtitle, projectId }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, user } = useAuth();
    const capture = useCaptureContext();
    // Iter-158.13.2.17b — Switcher now merges legacy projects with
    // canonical Route 2 workspaces. `switcherEntries` carries three
    // arrays so the render can group them under separate section
    // labels without a second pass.
    // Iter-158.13.2.20f.32 — Split Route 2 workspaces into two
    // access classes so the dropdown mirrors MyProjects's PROJECTS
    // vs PRODUCTION MANAGEMENT (SCRIPT CHANGES) information
    // hierarchy. Same production can appear in both lists when the
    // caller holds both accesses (mirrors MyProjects deptRows +
    // hubRows).
    const [switcherEntries, setSwitcherEntries] = useState({
        legacyItems: [],
        projectItems: [],
        productionManagementItems: [],
    });
    // Iter-158.5.2.a — Namespace Seal. All in-project navigation
    // (Link `to`, navigate(), backHref) must stay inside the current
    // route base so a beta H&M workspace click never leaks back to
    // the legacy `/projects/…` URL. Legacy value is `/projects`;
    // beta value is `/beta/hm-app/projects`.
    const routeBase = useRouteBase();
    // Iter-158.5.3 — Beta Read Parity. Project-scoped fetches
    // (backup status, membership, etc.) also route through the
    // shared apiBase so the ProjectHeader itself never leaks a
    // legacy `/api/projects/…` GET while mounted inside beta.
    const apiBase = useApiBase();
    // iter-58 — informational only. We DO NOT block any capture/upload
    // affordance based on these values; the AssignmentDialog handles
    // offline failures by enqueuing for future retry. The header pill
    // is purely status feedback.
    const online = useNetworkStatus();
    const { count: pendingCount } = useOfflineQueue({ projectId });
    // iter-74 PART 1 — live "Uploading…" / "Saved" pulse driven by
    // the AssignmentDialog save flow. We listen for the global
    // `onsetflow:upload-state` events; "saved" auto-decays to
    // `idle` after 1.5s so the pill returns to its normal look
    // (or to the offline-pending state if items are queued).
    const [uploadState, setUploadState] = useState("idle"); // 'idle' | 'uploading' | 'saved'
    useEffect(() => {
        const handler = (ev) => {
            const detail = ev?.detail || {};
            if (detail.project_id && projectId && detail.project_id !== projectId) {
                return;
            }
            if (detail.state === "uploading" || detail.state === "saved" || detail.state === "idle") {
                setUploadState(detail.state);
            }
        };
        window.addEventListener("onsetflow:upload-state", handler);
        return () =>
            window.removeEventListener("onsetflow:upload-state", handler);
    }, [projectId]);
    useEffect(() => {
        if (uploadState !== "saved") return;
        const t = setTimeout(() => setUploadState("idle"), 1500);
        return () => clearTimeout(t);
    }, [uploadState]);

    useEffect(() => {
        if (!projectId) return;
        // Iter-158.13.2.17b — Switcher data sources.
        // Legacy list (`GET /api/projects`) and Route 2 workspaces
        // (`GET /api/beta/me/workspaces?archived=false`) are fetched
        // in parallel. Each side is independently `.catch()`'d to an
        // empty result so a 403 / network failure on one source
        // never blanks the other.
        // Iter-158.13.2.20f.32 — `buildSwitcherEntries` now partitions
        // Route 2 workspace rows into `projectItems` (dept-level
        // access) and `productionManagementItems` (Hub / Production-
        // scope access) instead of the old Hub-wins collapse. Same
        // production can appear under both lists — mirrors the
        // MyProjects PROJECTS + PRODUCTION MANAGEMENT (SCRIPT CHANGES)
        // structure. No Route 2 routes are reconstructed here; the
        // workspace payload's own `landing_url` is trusted.
        let alive = true;
        (async () => {
            const [legacyRes, betaRes] = await Promise.all([
                api.get("/projects").catch(() => ({ data: [] })),
                api
                    .get("/beta/me/workspaces", { params: { archived: "false" } })
                    .catch(() => ({ data: { workspaces: [] } })),
            ]);
            if (!alive) return;
            const legacy = Array.isArray(legacyRes?.data) ? legacyRes.data : [];
            const beta = Array.isArray(betaRes?.data?.workspaces)
                ? betaRes.data.workspaces
                : [];
            setSwitcherEntries(buildSwitcherEntries(legacy, beta));
        })();
        return () => {
            alive = false;
        };
    }, [projectId]);

    // Iter-116 PART 5 — header backup status chip + soft prompt.
    // Lightweight probe that runs once per project nav. Members get
    // 403 from this endpoint; we silently ignore so non-admins see
    // no chip.
    const [backupStatus, setBackupStatus] = useState(null); // null=unknown, 'active'|'paused'|'none'
    const [backupCanManage, setBackupCanManage] = useState(false);
    // Iter-158.13.2.20f.22 · UAT B — Department context must show
    // truthful Production backup state without exposing management
    // controls. The endpoint returns `managed_by: "production"` on
    // the H&M mount so the chip renders as a plain badge and DOES
    // NOT link to a Connect / Pause / Disconnect flow.
    const [backupManagedByProduction, setBackupManagedByProduction] = useState(false);
    // Iter-118.13 PART 3 — global search dialog. Opened from the
    // header search icon and the Cmd/Ctrl+K shortcut.
    const [searchOpen, setSearchOpen] = useState(false);
    useEffect(() => {
        const onKey = (ev) => {
            if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "k") {
                ev.preventDefault();
                setSearchOpen((v) => !v);
            }
            if (ev.key === "/" && !ev.metaKey && !ev.ctrlKey && !ev.altKey) {
                const tag = (ev.target?.tagName || "").toLowerCase();
                const editable = ev.target?.isContentEditable;
                if (tag === "input" || tag === "textarea" || editable) return;
                ev.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    const loadBackupStatus = useCallback(async () => {
        if (!projectId) {
            setBackupStatus(null);
            return;
        }
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/backup/connection`,
            );
            // Iter-158.13.2.20f.22 · UAT B — server-authoritative
            // truth. The Department mount returns
            // `can_manage: false, managed_by: "production"` so
            // Contributors see the truthful chip state (from
            // `connection.enabled`) but the chip renders as a
            // static badge, not a Link, and the Connect / Pause /
            // Disconnect flows stay behind Production Management.
            const canMgr = data?.can_manage === undefined ? true : !!data.can_manage;
            setBackupCanManage(canMgr);
            setBackupManagedByProduction(data?.managed_by === "production");
            if (!data.connection) setBackupStatus("none");
            else if (!data.connection.enabled) setBackupStatus("paused");
            else setBackupStatus("active");
        } catch {
            setBackupCanManage(false);
            setBackupManagedByProduction(false);
        }
    }, [projectId, apiBase]);

    useEffect(() => {
        // Iter-157I — fetch deferred to next tick so the strict
        // `set-state-in-effect` lint doesn't trace the
        // setBackupStatus / setBackupCanManage chain back into the
        // effect body.
        const t = setTimeout(loadBackupStatus, 0);
        return () => clearTimeout(t);
    }, [loadBackupStatus]);

    const isProjectRoot =
        projectId && location.pathname === `/projects/${projectId}`;

    const goBack = () => {
        // Iter-158.13.2.20f.5 #5 — Production Management subpages
        // (Storage / Restore Points / Complete Export / Team & Access
        // / Documents / Schedule) ALWAYS return to their Production
        // Hub, not to whatever preceded them in history. Human UAT
        // proved that after clicking Storage from My Projects, Back
        // was returning to My Projects rather than the Production
        // Management Hub — deep-linking from the hub card was
        // pushing straight past `/beta/projects/{pid}`.
        //
        // Match is defensive: any `/beta/projects/{pid}/<sub>` URL
        // where <sub> is one of the known Production Management
        // subroutes routes back to the hub. Everything else keeps
        // the existing history-aware behaviour.
        const productionMgmtSubs = [
            "backups", "attic", "exports", "settings",
            "supervisor-notes", "schedule",
        ];
        if (projectId) {
            const m = location.pathname.match(
                /^\/beta\/projects\/([^/]+)\/([^/]+)/,
            );
            if (m && m[1] === projectId && productionMgmtSubs.includes(m[2])) {
                navigate(`/beta/projects/${projectId}`);
                return;
            }
        }
        // React Router v6 stores `{idx, key}` in window.history.state.
        // `idx > 0` means we have at least one prior in-app entry, so it's
        // safe to step back through the SPA history. (`document.referrer`
        // is unreliable here — it reflects the page that loaded the SPA
        // shell, not the React Router history.)
        const idx = window.history.state?.idx;
        if (typeof idx === "number" && idx > 0) {
            navigate(-1);
            return;
        }
        // No in-app history — most likely a deep link or a fresh tab.
        // Land the user on the project root rather than blowing them out
        // to a blank app shell.
        if (projectId) navigate(`${routeBase}/${projectId}`);
        else navigate("/");
    };

    return (
        <header
            data-testid="project-header"
            className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10"
        >
            {/* Iter-158.13.2.20f.42-kiwi4 — persistent OnSetFlow
                product masthead, aligned to the human-approved
                My Projects header reference: text-2xl wordmark on
                a px-5 left edge (matches the normal content edge
                used across the app). Two-line architecture
                preserved: this masthead sits above the operational
                nav row below. No routes, permissions,
                notifications, backup, flags, search, upload,
                settings or navigation controls are altered by this
                presentation change. */}
            <div
                data-testid="onsetflow-masthead"
                className="max-w-2xl mx-auto px-5 h-11 flex items-center border-b border-white/5"
            >
                <OnSetFlowWordmark
                    testid="onsetflow-masthead-wordmark"
                    className="text-2xl select-none"
                />
            </div>
            <div className="max-w-2xl mx-auto px-3 h-14 flex items-center gap-1.5">
                {projectId && !isProjectRoot ? (
                    <button
                        data-testid="back-button"
                        onClick={goBack}
                        title="Back"
                        className="tap inline-flex items-center justify-center rounded-md h-10 w-10 text-neutral-300 hover:text-white hover:bg-white/5"
                    >
                        <ArrowLeft size={16} />
                    </button>
                ) : null}
                {/* Home tile is now persistent on every project screen so
                    users can never feel lost — single tap returns them to
                    the project overview (Scenes / Cast / Script). */}
                <button
                    data-testid="home-button"
                    onClick={() =>
                        navigate(projectId ? `${routeBase}/${projectId}` : "/")
                    }
                    title={projectId ? "Project overview" : "My Projects"}
                    className="tap inline-flex items-center justify-center rounded-md h-10 w-10 text-neutral-300 hover:text-white hover:bg-white/5"
                >
                    <Home size={16} />
                </button>

                {/* Iter-158.13.2.20f.23 — OnSetFlow wordmark removed
                    from header per product direction. The Home glyph
                    at the far left now anchors the navigation
                    hierarchy on its own; the visual sentence reduces
                    to "Home › Project › Section". Iter-138's inline
                    wordmark span was deleted rather than hidden so
                    the DOM stays flat and there is no residual
                    reserved width on any viewport. */}

                {projectId ? (
                    <div className="min-w-0 flex-1 flex items-center justify-center gap-1">
                        <Link
                            to={`${routeBase}/${projectId}`}
                            data-testid="project-header-name-link"
                            className="tap min-w-0 text-center inline-block px-2 py-1 rounded-md hover:bg-white/5"
                        >
                            <div
                                data-testid="project-header-name"
                                className="truncate font-display text-sm font-bold text-white"
                            >
                            {/* Iter-158.13.2.20f.1 (#15) —
                                skeleton state instead of a bare
                                dash while the Hub GET is in flight.
                                A dash implied the header was giving
                                up on the Production name; the pulse
                                makes it clear the label is loading. */}
                            {projectName ? (
                                projectName
                            ) : (
                                <span
                                    data-testid="project-header-name-loading"
                                    className="inline-block h-3 w-24 rounded bg-white/10 animate-pulse align-middle"
                                    aria-label="Loading project name"
                                />
                            )}
                            </div>
                            {subtitle ? (
                                <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 -mt-0.5">
                                    {subtitle}
                                </div>
                            ) : null}
                        </Link>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="project-selector"
                                    title="Switch project"
                                    className="tap inline-flex items-center justify-center rounded-md h-8 w-7 text-neutral-400 hover:text-white hover:bg-white/5"
                                >
                                    <ChevronDown size={14} />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                data-testid="project-selector-menu"
                                align="center"
                                className="w-[280px] bg-popover text-neutral-200 border-white/10 shadow-2xl"
                            >
                                {/* Iter-158.13.2.20f.32 — dropdown IA
                                    repair. Mirrors MyProjects:
                                      PROJECTS
                                        New project
                                        Join project
                                      ─── ordinary divider ───
                                      PROJECTS
                                        <project-access productions>
                                        (Legacy)
                                        <legacy projects>
                                      ═══ STRONG divider ═══
                                      PRODUCTION MANAGEMENT (SCRIPT CHANGES)
                                        <Hub-access productions>
                                      ─── ordinary divider ───
                                      Theme  Dark | Light
                                      Sign out · email
                                    "Switch project" title, "All my
                                    projects", and "Project settings"
                                    are removed from this dropdown
                                    per Alex's spec — the Home
                                    control and Production Management
                                    surface those destinations. */}
                                <DropdownMenuLabel
                                    data-testid="project-menu-projects-actions-label"
                                    className="text-[10px] uppercase tracking-[0.25em] text-neutral-500"
                                >
                                    Projects
                                </DropdownMenuLabel>
                                <DropdownMenuItem
                                    data-testid="project-menu-new"
                                    onClick={() => navigate("/projects/new")}
                                    className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                >
                                    <Plus size={12} className="mr-2" /> New project
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="project-menu-join"
                                    onClick={() => navigate("/projects/join")}
                                    className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                >
                                    <KeyRound size={12} className="mr-2" /> Join project
                                </DropdownMenuItem>
                                {/* Iter-158.13.2.20f.34 — Second PROJECTS
                                    section. Always rendered (even when
                                    the caller has no dept-access
                                    productions) so the `All My
                                    Projects` escape route back to the
                                    existing My Projects landing is
                                    universally reachable. Individual
                                    Project entries + optional Legacy
                                    sub-label render only when their
                                    respective lists have members. */}
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuLabel
                                    data-testid="project-switch-beta-section-label"
                                    className="text-[10px] uppercase tracking-[0.25em] text-neutral-500"
                                >
                                    Projects
                                </DropdownMenuLabel>
                                <DropdownMenuItem
                                    data-testid="project-menu-all-my-projects"
                                    onClick={() => navigate("/")}
                                    className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                >
                                    <Home size={12} className="mr-2" /> All My Projects
                                </DropdownMenuItem>
                                {switcherEntries.projectItems.map((p) => {
                                    const active = p.id === projectId;
                                    return (
                                        <DropdownMenuItem
                                            key={`beta-${p.id}`}
                                            data-testid={`project-switch-beta-${p.id}`}
                                            onClick={() => navigate(p.landing_url)}
                                            className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                        >
                                            <div className="flex items-center gap-2 w-full">
                                                <div className="truncate flex-1">{p.name}</div>
                                                {active ? (
                                                    <Check size={12} className="text-primary" />
                                                ) : null}
                                            </div>
                                        </DropdownMenuItem>
                                    );
                                })}
                                {switcherEntries.legacyItems.length > 0 ? (
                                    <>
                                        <DropdownMenuLabel
                                            data-testid="project-switch-legacy-section-label"
                                            className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 px-2 pt-2 pb-0"
                                        >
                                            Legacy
                                        </DropdownMenuLabel>
                                        {switcherEntries.legacyItems.map((p) => {
                                            const active = p.id === projectId;
                                            return (
                                                <DropdownMenuItem
                                                    key={`legacy-${p.id}`}
                                                    data-testid={`project-switch-legacy-${p.id}`}
                                                    onClick={() => navigate(p.landing_url)}
                                                    className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                                >
                                                    <div className="flex items-center gap-2 w-full">
                                                        <div className="truncate flex-1">{p.name}</div>
                                                        {active ? (
                                                            <Check size={12} className="text-primary" />
                                                        ) : null}
                                                    </div>
                                                </DropdownMenuItem>
                                            );
                                        })}
                                    </>
                                ) : null}
                                {switcherEntries.productionManagementItems.length > 0 ? (
                                    <>
                                        {/* STRONG divider — deliberately
                                            more prominent than the
                                            ordinary DropdownMenuSeparator
                                            so PROJECTS vs PRODUCTION
                                            MANAGEMENT reads as two
                                            distinct classes of access
                                            (Carla IA repair).
                                            Iter-158.13.2.20f.32b —
                                            post-deploy UAT: at real
                                            render the h-px @ white/25
                                            rule wasn't reading as
                                            "clearly visible" against the
                                            surrounding white/10 separators.
                                            Bumped to h-0.5 (2px) @
                                            white/40, full-width (dropped
                                            mx-2 inset), and additional
                                            vertical breathing room. */}
                                        <div
                                            data-testid="project-menu-strong-divider"
                                            role="separator"
                                            aria-orientation="horizontal"
                                            className="my-3 h-0.5 bg-white/40"
                                        />
                                        <DropdownMenuLabel
                                            data-testid="project-menu-pm-section-label"
                                            className="text-[10px] uppercase tracking-[0.25em] text-neutral-300 font-semibold leading-tight"
                                        >
                                            {/* Iter-158.13.2.20f.32b —
                                                deliberate two-line heading.
                                                Real-viewport UAT (both
                                                desktop and mobile) showed
                                                the 30-character single
                                                string wrapping mid-word in
                                                the 280px letter-spaced
                                                container. Two stacked
                                                whitespace-nowrap spans
                                                lock the break at exactly
                                                the intended point:
                                                    PRODUCTION MANAGEMENT
                                                    (SCRIPT CHANGES)
                                                on every viewport. */}
                                            <span
                                                data-testid="project-menu-pm-section-label-line1"
                                                className="block whitespace-nowrap"
                                            >
                                                Production Management
                                            </span>
                                            <span
                                                data-testid="project-menu-pm-section-label-line2"
                                                className="block whitespace-nowrap text-neutral-400"
                                            >
                                                (Script Changes)
                                            </span>
                                        </DropdownMenuLabel>
                                        {switcherEntries.productionManagementItems.map((p) => {
                                            const active = p.id === projectId;
                                            return (
                                                <DropdownMenuItem
                                                    key={`hub-${p.id}`}
                                                    data-testid={`project-switch-pm-${p.id}`}
                                                    onClick={() => navigate(p.landing_url)}
                                                    className="text-neutral-200 focus:bg-white/5 focus:text-white"
                                                >
                                                    <div className="flex items-center gap-2 w-full">
                                                        <div className="truncate flex-1">{p.name}</div>
                                                        {active ? (
                                                            <Check size={12} className="text-primary" />
                                                        ) : null}
                                                    </div>
                                                </DropdownMenuItem>
                                            );
                                        })}
                                    </>
                                ) : null}
                                <DropdownMenuSeparator className="bg-white/10" />
                                {/* Iter-116b PART 11 — ThemeToggle moved
                                    out of the visible header (kept it
                                    minimal) and into the project drop-
                                    down menu. Available from every
                                    project surface but no longer
                                    crowding the top bar. */}
                                <div className="px-2 py-1.5 flex items-center justify-between text-xs text-neutral-200">
                                    <span className="text-neutral-400">Theme</span>
                                    <ThemeToggle
                                        testid="project-menu-theme-toggle"
                                        label={false}
                                    />
                                </div>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem
                                    data-testid="project-menu-signout"
                                    onClick={() => {
                                        logout();
                                        navigate("/login", { replace: true });
                                    }}
                                    className="text-neutral-300 focus:bg-white/5 focus:text-white"
                                >
                                    <LogOut size={12} className="mr-2" />
                                    <span className="truncate">
                                        Sign out · {user?.email}
                                    </span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                ) : (
                    <div className="flex-1 text-center">
                        <div className="truncate font-display text-sm font-bold text-white">
                            {projectName ? (
                                projectName
                            ) : (
                                <span
                                    data-testid="project-header-name-loading-mobile"
                                    className="inline-block h-3 w-24 rounded bg-white/10 animate-pulse align-middle"
                                    aria-label="Loading project name"
                                />
                            )}
                        </div>
                    </div>
                )}

                {projectId ? (
                    <>
                        {/* iter-58 + iter-74 — combined header status pill.
                            Renders ONLY when at least ONE of:
                              • the device is offline,
                              • there are pending offline uploads queued,
                              • a fresh save is in flight (`uploading`),
                              • a save just completed (`saved`, 1.5s decay).
                            Order of precedence (most-attention-grabbing
                            first): uploading → saved → offline → pending.
                            iter-74 PART 1 — the user complained the
                            cloud icon + counter alone was opaque. We now
                            show the actual lifecycle: spinner+"Uploading"
                            during the round-trips, green check+"Saved"
                            for 1.5s on success, then back to pending /
                            offline / idle. */}
                        {uploadState === "uploading" ? (
                            <div
                                data-testid="header-status-pill"
                                data-state="uploading"
                                title="Saving your image…"
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0 bg-primary/15 text-primary border border-primary/30"
                            >
                                <Loader2 size={11} className="animate-spin" />
                                <span data-testid="header-status-text">
                                    Uploading…
                                </span>
                            </div>
                        ) : uploadState === "saved" ? (
                            <div
                                data-testid="header-status-pill"
                                data-state="saved"
                                title="Saved"
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0 bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
                            >
                                <Check size={11} />
                                <span data-testid="header-status-text">
                                    Saved
                                </span>
                            </div>
                        ) : !online || pendingCount > 0 ? (
                            <div
                                data-testid="offline-pill"
                                title={
                                    !online
                                        ? "Offline — uploads will queue"
                                        : `${pendingCount} upload${
                                              pendingCount === 1 ? "" : "s"
                                          } waiting for connection. Reconnect to retry.`
                                }
                                className={[
                                    "inline-flex items-center gap-1 h-7 px-2 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0",
                                    !online
                                        ? "bg-amber-400/15 text-amber-200 border border-amber-400/30"
                                        : "bg-amber-400/10 text-amber-200 border border-amber-400/30",
                                ].join(" ")}
                            >
                                {!online ? (
                                    <>
                                        <WifiOff size={11} />
                                        <span className="hidden sm:inline">
                                            Offline · {pendingCount} waiting
                                        </span>
                                        <span className="sm:hidden">
                                            {pendingCount}
                                        </span>
                                    </>
                                ) : (
                                    /* Iter-157I P0 — Truthful queue
                                       states. Online + pending means
                                       the flush worker will retry on
                                       the next tick; we surface a
                                       calm "Retrying…" label and a
                                       manual "Retry now" affordance
                                       via click. */
                                    <button
                                        type="button"
                                        data-testid="offline-pill-retry"
                                        onClick={async () => {
                                            try {
                                                const { flushPendingUploads } = await import("../lib/offline");
                                                const { api } = await import("../lib/api");
                                                await flushPendingUploads(api);
                                            } catch { /* swallow */ }
                                        }}
                                        title="Retry queued uploads now"
                                        className="inline-flex items-center gap-1 hover:opacity-90"
                                    >
                                        <UploadCloud size={11} />
                                        <span data-testid="offline-pill-count">
                                            {pendingCount}
                                        </span>
                                        <span className="hidden sm:inline">
                                            retrying — tap to retry now
                                        </span>
                                    </button>
                                )}
                            </div>
                        ) : null}
                        {/* Laptop-only quick-add. Hidden on small screens
                            because the floating Capture button is the
                            primary affordance there. */}
                        {/* Iter-118.13 PART 3 — global search trigger.
                            Always present in the header so the user can
                            jump from any surface to any other surface
                            via one search. Cmd/Ctrl+K and "/" shortcut
                            open the same dialog from anywhere. */}
                        {projectId ? (
                            <button
                                type="button"
                                data-testid="header-global-search-btn"
                                onClick={() => setSearchOpen(true)}
                                title="Search this project (⌘K / Ctrl+K)"
                                aria-label="Search this project"
                                className="tap relative inline-flex items-center justify-center rounded-md h-10 w-10 text-neutral-300 hover:text-white hover:bg-white/5"
                            >
                                <Search size={16} />
                            </button>
                        ) : null}
                        <HeaderAddMenu />

                        {/* Iter-114 — Flag bell with active-flag count.
                            Sits BETWEEN HeaderAddMenu and the
                            Unassigned inbox so the attention layer
                            has a permanent home next to the other
                            inbound surfaces. */}
                        <FlagBell projectId={projectId} />

                        {/* Iter-116 PART 1 — Global flag panel mount.
                            Listens to ?flag={id} in the URL, fetches
                            that flag, and overlays its full context +
                            actions over whatever entity the user just
                            navigated to. Lives here so EVERY project
                            page gets it for free. */}
                        <FlagPanel projectId={projectId} />

                        {/* Iter-118.13 PART 3 — Global search dialog mount. */}
                        {projectId ? (
                            <GlobalSearchDialog
                                open={searchOpen}
                                onOpenChange={setSearchOpen}
                                projectId={projectId}
                            />
                        ) : null}

                        {/* Iter-116 PART 5 — Backup status chip. Shows
                            "Backups Active" (emerald) or "No Backup"
                            (amber).
                            Iter-158.13.2.20f.22 · UAT B — the chip is
                            visible on BOTH Production Management and
                            Department contexts because backup truth
                            belongs to the Production and Department
                            users must be able to SEE it. On Department
                            mounts (`managed_by === "production"`) the
                            chip renders as a plain, non-clickable
                            badge with an explanatory title — Connect /
                            Pause / Disconnect stay behind Production
                            Management. */}
                        {(backupCanManage || backupManagedByProduction) && backupStatus ? (
                            backupManagedByProduction ? (
                                <span
                                    data-testid="header-backup-status"
                                    data-status={backupStatus}
                                    data-managed-by="production"
                                    title={
                                        backupStatus === "active"
                                            ? "Production backup is active. Managed through Production Management."
                                            : backupStatus === "paused"
                                                ? "Production backup is paused. Managed through Production Management."
                                                : "No external Production backup connected. Ask your Production admin to connect one."
                                    }
                                    className={`tap hidden md:inline-flex items-center gap-1.5 rounded-md h-9 px-2 text-[10px] uppercase tracking-[0.2em] border cursor-default ${
                                        backupStatus === "active"
                                            ? "text-emerald-300 bg-emerald-500/8 border-emerald-500/25"
                                            : "text-amber-300 bg-amber-500/8 border-amber-500/30"
                                    }`}
                                >
                                    {backupStatus === "active" ? (
                                        <>
                                            <Cloud size={12} />
                                            Backups
                                        </>
                                    ) : backupStatus === "paused" ? (
                                        <>
                                            <CloudOff size={12} />
                                            Paused
                                        </>
                                    ) : (
                                        <>
                                            <CloudOff size={12} />
                                            No backup
                                        </>
                                    )}
                                </span>
                            ) : (
                                <Link
                                    to={`${routeBase}/${projectId}/backups`}
                                    data-testid="header-backup-status"
                                    data-status={backupStatus}
                                    title={
                                        backupStatus === "active"
                                            ? "External backup is active"
                                            : backupStatus === "paused"
                                                ? "External backup is paused"
                                                : "No external backup connected"
                                    }
                                    className={`tap hidden md:inline-flex items-center gap-1.5 rounded-md h-9 px-2 text-[10px] uppercase tracking-[0.2em] border ${
                                        backupStatus === "active"
                                            ? "text-emerald-300 bg-emerald-500/8 border-emerald-500/25 hover:bg-emerald-500/15"
                                            : "text-amber-300 bg-amber-500/8 border-amber-500/30 hover:bg-amber-500/15"
                                    }`}
                                >
                                    {backupStatus === "active" ? (
                                        <>
                                            <Cloud size={12} />
                                            Backups
                                        </>
                                    ) : backupStatus === "paused" ? (
                                        <>
                                            <CloudOff size={12} />
                                            Paused
                                        </>
                                    ) : (
                                        <>
                                            <CloudOff size={12} />
                                            No backup
                                        </>
                                    )}
                                </Link>
                            )
                        ) : null}

                        <Link
                            to={`${routeBase}/${projectId}/unassigned`}
                            data-testid="unassigned-top-btn"
                            title="Unassigned"
                            className="tap relative inline-flex items-center justify-center rounded-md h-10 w-10 text-neutral-300 hover:text-white hover:bg-white/5"
                        >
                            <Inbox size={16} />
                            {capture?.unassignedCount > 0 ? (
                                <span
                                    data-testid="unassigned-top-count"
                                    className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-amber-400 text-[9px] font-bold text-neutral-900 flex items-center justify-center"
                                >
                                    {capture.unassignedCount > 9 ? "9+" : capture.unassignedCount}
                                </span>
                            ) : null}
                        </Link>

                        {/* iter-47: Settings moved out of the capture footer
                            into the top bar, sitting alongside the project
                            dropdown + Unassigned indicator. Visually
                            secondary (same neutral hover treatment as the
                            other admin controls) so it doesn't compete with
                            the primary capture flow.
                            Iter-118.4 — pending permission requests now
                            surface as a tiny RED DOT here (owner-only signal,
                            different visual system from the FlagBell triangle
                            so we keep the "one attention triangle" rule). */}
                        <Link
                            to={`${routeBase}/${projectId}/settings`}
                            data-testid="settings-top-btn"
                            title={
                                capture?.pendingPermissionCount > 0
                                    ? `${capture.pendingPermissionCount} permission request${
                                          capture.pendingPermissionCount === 1 ? "" : "s"
                                      } pending`
                                    : "Project settings"
                            }
                            aria-label="Project settings"
                            className="tap relative inline-flex items-center justify-center rounded-md h-10 w-10 text-neutral-300 hover:text-white hover:bg-white/5"
                        >
                            <Settings size={16} />
                            {capture?.pendingPermissionCount > 0 ? (
                                <span
                                    data-testid="settings-perm-dot"
                                    aria-hidden="true"
                                    className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-background"
                                />
                            ) : null}
                        </Link>
                    </>
                ) : (
                    <button
                        data-testid="header-logout"
                        onClick={() => {
                            logout();
                            navigate("/login", { replace: true });
                        }}
                        title={user?.email}
                        className="tap inline-flex items-center justify-center rounded-md h-10 w-10 text-neutral-400 hover:text-white hover:bg-white/5"
                    >
                        <LogOut size={14} />
                    </button>
                )}
            </div>
        </header>
    );
}

export default function ProjectHeader({ projectName, subtitle }) {
    const { projectId } = useParams();
    if (!projectId) {
        return <TopBar projectName={projectName} subtitle={subtitle} projectId={null} />;
    }
    return (
        <CaptureProvider projectId={projectId}>
            <TopBar projectName={projectName} subtitle={subtitle} projectId={projectId} />
            {/* Iter-156A.5 Phase 5 — archive read-only banner. Renders
                only when the project is archived. Owner sees the
                Unarchive CTA; non-owners see calm read-only copy. */}
            <ArchiveBanner />
            <CaptureFab projectId={projectId} />
            {/* Iter-116 PART 5 — Soft backup-safety prompt for owners
                /admins on first project visit (per day). Members see
                nothing. Never blocks; just nudges. */}
            <BackupSafetyModal
                projectId={projectId}
                canManage={true /* the modal probes & 403s for members */}
                enabled
            />
        </CaptureProvider>
    );
}
